import {Alert, DataStatus, showNotification, type AlertVariants} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';

import {
  auctionApi,
  useAbandonVehicleAuctionMutation,
  useConfirmVehicleAuctionWinnerMutation,
  useDeclineVehicleAuctionOfferMutation,
  useEndVehicleAuctionBiddingMutation,
  useGetVehicleAuctionQuery,
  useKeepVehicleAuctionInStockMutation,
  usePublishVehicleAuctionMutation,
  useRelaunchVehicleAuctionMutation,
  useRequestVehicleAuctionSaleBusinessCaseMutation,
  type DeclineOfferRequestBody,
  type PublishAuctionRequestBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

import {
  auctionBanner,
  auctionCurrentStep,
  canSelectOffer,
  initialOfferId,
  isAuctionAwarded,
  isOfferRejected,
  type AuctionBanner,
  type AuctionStep,
} from '../model/auctionView';
import type {AbandonReason, AuctionRule, AuctionView} from '../model/auctionView.types';
import {AbandonAuctionDialog} from './components/AbandonAuctionDialog';
import {AuctionHeader} from './components/AuctionHeader';
import {AuctionPurchaseCards} from './components/AuctionPurchaseCards';
import {EndAuctionDialog} from './components/EndAuctionDialog';
import {useAuctionFormat} from './components/useAuctionFormat';
import {usePurchaseCase, type PurchaseCase} from './components/usePurchaseCase';
import {ConfirmationStep} from './steps/ConfirmationStep';
import {OffersStep} from './steps/OffersStep';
import {PreparationStep} from './steps/PreparationStep';

export type VehicleAuctionProps = {vehicleId: string};

// The English defaults of the AuctionRuleViolation codes. The BE message names only the code.
const AUCTION_RULE_MESSAGE: Record<AuctionRule, string> = {
  WRONG_STATE: 'The auction changed in the meantime. Review its current state.',
  BIDDING_CLOSED: 'Bidding is closed.',
  OFFER_NOT_HIGHER: 'The offer must be higher than the current offer.',
  INVALID_AMOUNT: 'The amount is not valid.',
  CURRENCY_MISMATCH: 'The offer currency is different from the auction currency.',
  OFFER_NOT_FOUND: 'The offer does not exist anymore.',
  CORRECTION_WINDOW_CLOSED: 'The time to correct the offer has ended.',
  CLOCK_NOT_DUE: 'The deadline has not passed yet.',
  INVALID_TERMS: 'The auction terms are not valid.',
  CAR_AUDIT_MISSING: 'The vehicle has no finished condition inspection.',
  VEHICLE_NOT_FOR_SALE: 'The vehicle is not for sale.',
  ROUND_ALREADY_OPEN: 'An auction round is already open for this vehicle.',
  PURCHASE_NOT_SIGNED: 'Sign the purchase contract first.',
  NOT_TRADE_IN: 'Only a trade-in vehicle can stay in stock.',
  INVALID_QUESTION: 'The question is not valid.',
  NOT_WINNER: 'This trader is not the confirmed buyer.',
  OFFER_REJECTED: 'This offer was rejected. Select another offer.',
  EVIDENCE_REQUIRED: 'Enter the evidence reference for this source.',
  SALE_PRICE_MISSING: 'Enter the sale price of the vehicle first.',
  SALE_BUSINESS_CASE_EXISTS: 'The sale business case exists already.',
  SALE_CURRENCY_MISMATCH: 'The sale currency is different from the offer currency.',
};

const isAuctionRule = (code: unknown): code is AuctionRule =>
  typeof code === 'string' && code in AUCTION_RULE_MESSAGE;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// Undefined: the error is not an auction rule violation (409 or 422), so the generic handler shows it.
function getAuctionErrorMessage(error: unknown) {
  if (!isRecord(error) || (error.status !== 409 && error.status !== 422) || !isRecord(error.data)) {
    return undefined;
  }
  const errors = error.data.errors;
  if (!Array.isArray(errors)) {
    return undefined;
  }
  const ruleError = errors.find((item) => isRecord(item) && isAuctionRule(item.code));
  if (isRecord(ruleError) && isAuctionRule(ruleError.code)) {
    return i18n.t(`entity.auction.rules.${ruleError.code}`, {
      defaultValue: AUCTION_RULE_MESSAGE[ruleError.code],
    });
  }
  const firstError = errors.find((item) => isRecord(item) && typeof item.message === 'string');
  return isRecord(firstError) && typeof firstError.message === 'string'
    ? firstError.message
    : undefined;
}

type BannerContent = {
  title: string;
  text: string;
  link: string;
  step: AuctionStep;
  variant: AlertVariants;
};

const ABANDON_REASON: Record<AbandonReason, string> = {
  OWNER_DECLINED: 'entity.auction.decision.reason.ownerDeclined',
  BUYER_WITHDREW: 'entity.auction.decision.reason.buyerWithdrew',
  VEHICLE_NOT_CONFORMING: 'entity.auction.decision.reason.vehicleNotConforming',
  OTHER: 'entity.auction.decision.reason.other',
};

const bannerContent = (
  key: AuctionBanner,
  view: AuctionView,
  formatDate: (date: string | null) => string
): BannerContent =>
  match(key)
    .returnType<BannerContent>()
    .with('prepare', 'offers', 'confirm', 'declined', (simple) => ({
      title: i18n.t(`entity.auction.banner.${simple}.title`),
      text: i18n.t(`entity.auction.banner.${simple}.text`),
      link: i18n.t(`entity.auction.banner.${simple}.link`),
      step: BANNER_STEP[simple],
      variant: simple === 'declined' ? 'warning' : 'info',
    }))
    .with('draftBlocked', () => ({
      title: i18n.t('entity.auction.banner.draftBlocked.title'),
      text: i18n.t('entity.auction.banner.draftBlocked.message', {
        check: view.blockers
          .filter(({action}) => action === 'publish')
          .map(({message}) => message)
          .join(', '),
      }),
      link: i18n.t('entity.auction.banner.prepare.link'),
      step: 'preparation',
      variant: 'warning',
    }))
    .with('waiting', () => ({
      title: i18n.t('entity.auction.banner.waiting.title', {defaultValue: 'Waiting for offers'}),
      text: i18n.t('entity.auction.banner.waiting.text', {
        defaultValue: 'The auction is open. Offers will appear here.',
      }),
      link: i18n.t('entity.auction.banner.offers.link'),
      step: 'offers',
      variant: 'info',
    }))
    .with('decision', () => ({
      title: i18n.t('entity.auction.banner.decision.title'),
      text: i18n.t('entity.auction.banner.decision.text', {
        defaultValue: 'Bidding has ended. Review the received offers within the decision window.',
      }),
      link: i18n.t('entity.auction.banner.offers.link'),
      step: 'offers',
      variant: 'info',
    }))
    .with('noOffers', () => ({
      title: i18n.t('entity.auction.banner.noOffers.title', {
        defaultValue: 'Auction ended without an available offer',
      }),
      text: i18n.t('entity.auction.banner.noOffers.text', {
        defaultValue: 'Review the auction and start another round when ready.',
      }),
      link: i18n.t('entity.auction.banner.ended.link'),
      step: 'preparation',
      variant: 'warning',
    }))
    .with('ended', () => ({
      title: i18n.t('entity.auction.banner.ended.title'),
      text: i18n.t('entity.auction.banner.ended.text'),
      link: i18n.t('entity.auction.banner.ended.link'),
      step: 'offers',
      variant: 'error',
    }))
    .with('awaitingVehicle', () => ({
      title: i18n.t('entity.auction.banner.awaiting_vehicle.title'),
      text: i18n.t('entity.auction.banner.awaiting_vehicle.message', {
        date: formatDate(view.clocks.paymentDeadlineAt),
      }),
      link: i18n.t('entity.auction.banner.confirm.link'),
      step: 'confirmation',
      variant: 'info',
    }))
    .with('awaitingPickup', () => ({
      title: i18n.t('entity.auction.banner.awaiting_pickup.title'),
      text: i18n.t('entity.auction.banner.awaiting_pickup.message', {
        date: formatDate(view.clocks.pickupDeadlineAt),
      }),
      link: i18n.t('entity.auction.banner.confirm.link'),
      step: 'confirmation',
      variant: 'info',
    }))
    .with('processEnded', () => {
      const reason = view.transaction?.cancelReason;
      return {
        title: i18n.t('entity.auction.banner.processEnded.title', {
          defaultValue: 'Auction process ended',
        }),
        // shortcut: the view has the abandon reason only after the award (transaction.cancelReason). Show it for every round when the BE adds it.
        text: match({state: view.state, reason})
          .with({state: 'kept'}, () => i18n.t('entity.auction.final.kept'))
          .with({reason: Pattern.string}, ({reason: code}) => i18n.t(ABANDON_REASON[code]))
          .otherwise(() =>
            i18n.t('entity.auction.banner.processEnded.text', {
              defaultValue: 'No sale was completed.',
            })
          ),
        link: i18n.t('entity.auction.banner.ended.link'),
        step: 'preparation',
        variant: 'info',
      };
    })
    .exhaustive();

const BANNER_STEP: Record<'prepare' | 'offers' | 'confirm' | 'declined', AuctionStep> = {
  prepare: 'preparation',
  offers: 'offers',
  confirm: 'confirmation',
  declined: 'offers',
};

interface AuctionWorkspaceProps {
  vehicleId: string;
  view: AuctionView;
  purchaseCase: PurchaseCase | null;
  onPublish: (body: PublishAuctionRequestBody) => Promise<unknown>;
  onEndBidding: () => void;
  onRelaunch: () => void;
  onConfirmWinner: (offerId: string) => Promise<AuctionView | null>;
  // Resolves to true when the BE recorded the decline.
  onDeclineOffer: (body: DeclineOfferRequestBody) => Promise<boolean>;
  onRequestSaleBusinessCase: () => void;
  onAbandon: (reason: AbandonReason) => Promise<boolean>;
  onKeepInStock: () => void;
}

function AuctionWorkspace(props: AuctionWorkspaceProps) {
  const [activeStep, setActiveStep] = useState<AuctionStep>(auctionCurrentStep(props.view));
  const [selectedOfferId, setSelectedOfferId] = useState(initialOfferId(props.view));
  const [isEndDialogOpen, setIsEndDialogOpen] = useState(false);
  const [isAbandonDialogOpen, setIsAbandonDialogOpen] = useState(false);
  const selectedOffer =
    props.view.offers.find((offer) => offer.offerId === selectedOfferId) ?? null;
  const isAwarded = isAuctionAwarded(props.view);
  const canSelect = canSelectOffer(props.view);
  const banner = auctionBanner(props.view, selectedOffer);
  const format = useAuctionFormat();
  const isSelectedRejected = isOfferRejected(selectedOffer);

  return (
    <VStack spacing={4}>
      <Show when={banner}>
        {(key) => {
          const content = bannerContent(key, props.view, (date) =>
            date === null ? '' : format.dateTime(date)
          );
          return (
            <Alert
              variant={content.variant}
              title={content.title}
              message={content.text}
              hyperlinks={[{title: content.link, onClick: () => setActiveStep(content.step)}]}
              data-testid="auction-banner"
            />
          );
        }}
      </Show>
      <AuctionPurchaseCards
        purchaseCase={props.purchaseCase}
        buyer={isSelectedRejected ? null : selectedOffer}
        isBuyerConfirmed={isAwarded}
      />
      {/* v11: the steps and the offers table go to the card edges, so the card has no body padding. */}
      {/* The base z-index keeps the sticky bar and the sort control below the activity sidebar. */}
      {/* No overflow clip: it makes the card the scroll box, so the selection bar sticks to the card, not to the viewport. */}
      <Box
        position="relative"
        zIndex="base"
        backgroundColor="palettes.white.10.100"
        borderRadius="small"
        boxShadow="elevation_1"
      >
        <AuctionHeader
          view={props.view}
          activeStep={activeStep}
          selectedOfferId={selectedOfferId}
          purchaseCase={props.purchaseCase}
          onStepChange={setActiveStep}
          onEndAuction={() => setIsEndDialogOpen(true)}
          onOpenNewRound={props.onRelaunch}
          onAbandon={() => setIsAbandonDialogOpen(true)}
          onKeepInStock={props.onKeepInStock}
        />
        {match(activeStep)
          .with('preparation', () => (
            <Box paddingHorizontal={4} paddingTop={6} paddingBottom={4}>
              <PreparationStep
                vehicleId={props.vehicleId}
                view={props.view}
                onPublish={props.onPublish}
              />
            </Box>
          ))
          .with('offers', () => (
            <OffersStep
              vehicleId={props.vehicleId}
              view={props.view}
              selectedOfferId={selectedOfferId}
              onSelect={canSelect ? setSelectedOfferId : undefined}
              onContinue={() => setActiveStep('confirmation')}
            />
          ))
          .with('confirmation', () => (
            <Box paddingHorizontal={4} paddingTop={6} paddingBottom={4}>
              <ConfirmationStep
                vehicleId={props.vehicleId}
                view={props.view}
                purchaseCase={props.purchaseCase}
                buyer={selectedOffer}
                onChangeSelection={() => setActiveStep('offers')}
                onConfirm={props.onConfirmWinner}
                onDeclineOffer={props.onDeclineOffer}
                onRequestSaleBusinessCase={props.onRequestSaleBusinessCase}
              />
            </Box>
          ))
          .exhaustive()}
      </Box>
      <EndAuctionDialog
        isOpen={isEndDialogOpen}
        roundNumber={props.view.roundNumber}
        onClose={() => setIsEndDialogOpen(false)}
        onEnd={props.onEndBidding}
      />
      <AbandonAuctionDialog
        isOpen={isAbandonDialogOpen}
        isAwarded={isAwarded}
        onClose={() => setIsAbandonDialogOpen(false)}
        onAbandon={props.onAbandon}
      />
    </VStack>
  );
}

export function VehicleAuction(props: VehicleAuctionProps) {
  const lastView = auctionApi.endpoints.getVehicleAuction.useQueryState({
    vehicleId: props.vehicleId,
  }).data;
  const query = useGetVehicleAuctionQuery(
    {vehicleId: props.vehicleId},
    {pollingInterval: lastView?.state === 'bidding' ? 30_000 : 0}
  );
  const purchaseCase = usePurchaseCase(props.vehicleId);
  const [publish] = usePublishVehicleAuctionMutation();
  const [endBidding] = useEndVehicleAuctionBiddingMutation();
  const [relaunch] = useRelaunchVehicleAuctionMutation();
  const [confirmWinner] = useConfirmVehicleAuctionWinnerMutation();
  const [declineOffer] = useDeclineVehicleAuctionOfferMutation();
  const [requestSaleBusinessCase] = useRequestVehicleAuctionSaleBusinessCaseMutation();
  const [abandon] = useAbandonVehicleAuctionMutation();
  const [keepInStock] = useKeepVehicleAuctionInStockMutation();

  const runAction = <T,>(action: Promise<T>) =>
    action.catch((error: ApiError) => {
      const message = getAuctionErrorMessage(error);
      if (message === undefined) {
        handleApiError(error);
        return;
      }
      showNotification.error(message);
      // A rule violation means that the cached view is stale, so the seller sees the real state.
      void query.refetch();
    });

  const view: AuctionView | undefined = query.data;

  return (
    <DataStatus
      isLoading={query.isLoading && view === undefined}
      isError={query.isError && view === undefined}
    >
      <Show when={view}>
        {(auctionView) => (
          <AuctionWorkspace
            // A new round or state opens the step that fits it.
            key={`${auctionView.roundId}-${auctionView.state}`}
            vehicleId={props.vehicleId}
            view={auctionView}
            purchaseCase={purchaseCase}
            onPublish={(body) =>
              runAction(
                publish({vehicleId: props.vehicleId, publishAuctionRequestBody: body}).unwrap()
              )
            }
            onEndBidding={() => void runAction(endBidding({vehicleId: props.vehicleId}).unwrap())}
            onRelaunch={() => void runAction(relaunch({vehicleId: props.vehicleId}).unwrap())}
            onConfirmWinner={(offerId) =>
              runAction(
                confirmWinner({
                  vehicleId: props.vehicleId,
                  confirmWinnerRequestBody: {offerId},
                }).unwrap()
              ).then((result) => result ?? null)
            }
            onDeclineOffer={(body) =>
              runAction(
                declineOffer({vehicleId: props.vehicleId, declineOfferRequestBody: body})
                  .unwrap()
                  .then(() => true)
              ).then((isRecorded) => isRecorded === true)
            }
            onRequestSaleBusinessCase={() =>
              void runAction(requestSaleBusinessCase({vehicleId: props.vehicleId}).unwrap())
            }
            onAbandon={(reason) =>
              runAction(
                abandon({vehicleId: props.vehicleId, abandonAuctionRequestBody: {reason}})
                  .unwrap()
                  .then(() => true)
              ).then((isRecorded) => isRecorded === true)
            }
            onKeepInStock={() => void runAction(keepInStock({vehicleId: props.vehicleId}).unwrap())}
          />
        )}
      </Show>
    </DataStatus>
  );
}
